import { PopalErrorCodes } from 'enum/error-codes/popal-error-codes.enum';

export interface PopalErrorMapping {
  code: PopalErrorCodes;
  message: string;
  isServiceError: boolean;
}

export interface PopalErrorMappingResult {
  userMessage: string;
  isServiceError: boolean;
  errorCode: string;
}

const POPAL_ERROR_MAPPINGS: Record<PopalErrorCodes, PopalErrorMapping> = {
  [PopalErrorCodes.SERVICE_UNAVAILABLE]: {
    code: PopalErrorCodes.SERVICE_UNAVAILABLE,
    message: 'The shipping service is temporarily unavailable. Please try again in a few minutes.',
    isServiceError: true,
  },
  [PopalErrorCodes.SERVICE_TIMEOUT]: {
    code: PopalErrorCodes.SERVICE_TIMEOUT,
    message: 'The shipping service timed out. Please try again in a few minutes.',
    isServiceError: true,
  },
  [PopalErrorCodes.CONNECTION_RESET]: {
    code: PopalErrorCodes.CONNECTION_RESET,
    message: 'Connection to shipping service was interrupted. Please try again.',
    isServiceError: true,
  },
  [PopalErrorCodes.CONNECTION_ERROR]: {
    code: PopalErrorCodes.CONNECTION_ERROR,
    message: 'Connection error occurred. Please try again.',
    isServiceError: true,
  },
  [PopalErrorCodes.DATABASE_TIMEOUT]: {
    code: PopalErrorCodes.DATABASE_TIMEOUT,
    message: 'The system is experiencing high load. Please try again in a few minutes.',
    isServiceError: true,
  },
  [PopalErrorCodes.CONSIGNEE_PHONE_REQUIRED]: {
    code: PopalErrorCodes.CONSIGNEE_PHONE_REQUIRED,
    message: 'Receiver phone number is required for delivery confirmation.',
    isServiceError: false,
  },
  [PopalErrorCodes.CONSIGNEE_PHONE_EMPTY]: {
    code: PopalErrorCodes.CONSIGNEE_PHONE_EMPTY,
    message: 'Receiver phone number cannot be empty.',
    isServiceError: false,
  },
  [PopalErrorCodes.CONSIGNEE_PHONE_INVALID]: {
    code: PopalErrorCodes.CONSIGNEE_PHONE_INVALID,
    message: 'Please provide a valid receiver phone number for delivery confirmation.',
    isServiceError: false,
  },
  [PopalErrorCodes.SHIPPER_PHONE_REQUIRED]: {
    code: PopalErrorCodes.SHIPPER_PHONE_REQUIRED,
    message: 'Sender phone number is required.',
    isServiceError: false,
  },
  [PopalErrorCodes.SHIPPER_PHONE_EMPTY]: {
    code: PopalErrorCodes.SHIPPER_PHONE_EMPTY,
    message: 'Sender phone number cannot be empty.',
    isServiceError: false,
  },
  [PopalErrorCodes.SHIPPER_PHONE_INVALID]: {
    code: PopalErrorCodes.SHIPPER_PHONE_INVALID,
    message: 'Please provide a valid sender phone number.',
    isServiceError: false,
  },
  [PopalErrorCodes.PHONE_REQUIRED]: {
    code: PopalErrorCodes.PHONE_REQUIRED,
    message: 'Phone number is required for the delivery address.',
    isServiceError: false,
  },
  [PopalErrorCodes.PHONE_EMPTY]: {
    code: PopalErrorCodes.PHONE_EMPTY,
    message: 'Phone number cannot be empty for the delivery address.',
    isServiceError: false,
  },
  [PopalErrorCodes.PHONE_INVALID]: {
    code: PopalErrorCodes.PHONE_INVALID,
    message: 'Please provide a valid phone number for the delivery address.',
    isServiceError: false,
  },
  [PopalErrorCodes.SENDER_NAME_REQUIRED]: {
    code: PopalErrorCodes.SENDER_NAME_REQUIRED,
    message: 'Sender name is required.',
    isServiceError: false,
  },
  [PopalErrorCodes.RECIPIENT_NAME_LENGTH]: {
    code: PopalErrorCodes.RECIPIENT_NAME_LENGTH,
    message: 'Recipient name must be between 2 and 64 characters.',
    isServiceError: false,
  },
  [PopalErrorCodes.RECIPIENT_NAME_TOO_LONG]: {
    code: PopalErrorCodes.RECIPIENT_NAME_TOO_LONG,
    message: 'Recipient name is too long. Please shorten to less than 40 characters.',
    isServiceError: false,
  },
  [PopalErrorCodes.RECIPIENT_NAME_TOO_SHORT]: {
    code: PopalErrorCodes.RECIPIENT_NAME_TOO_SHORT,
    message: 'Recipient name is too short. Please provide at least 2 characters.',
    isServiceError: false,
  },
  [PopalErrorCodes.SENDER_NAME_TOO_SHORT]: {
    code: PopalErrorCodes.SENDER_NAME_TOO_SHORT,
    message: 'Sender name is too short. Please provide at least 2 characters.',
    isServiceError: false,
  },
  [PopalErrorCodes.CITY_TOO_LONG]: {
    code: PopalErrorCodes.CITY_TOO_LONG,
    message: 'City name is too long. Please shorten to less than 31 characters.',
    isServiceError: false,
  },
  [PopalErrorCodes.INVALID_POSTCODE]: {
    code: PopalErrorCodes.INVALID_POSTCODE,
    message: 'Please provide a valid UK postcode.',
    isServiceError: false,
  },
  [PopalErrorCodes.WEIGHT_REQUIRED]: {
    code: PopalErrorCodes.WEIGHT_REQUIRED,
    message: 'Package weight is required.',
    isServiceError: false,
  },
  [PopalErrorCodes.WEIGHT_INVALID]: {
    code: PopalErrorCodes.WEIGHT_INVALID,
    message: 'Package weight must be greater than 0.',
    isServiceError: false,
  },
  [PopalErrorCodes.WEIGHT_MISMATCH]: {
    code: PopalErrorCodes.WEIGHT_MISMATCH,
    message: 'There is a weight mismatch in your shipment. Please verify the package weight.',
    isServiceError: false,
  },
  [PopalErrorCodes.WEIGHT_REQUIRED_NI]: {
    code: PopalErrorCodes.WEIGHT_REQUIRED_NI,
    message: 'Shipments to Northern Ireland require weight information for all items.',
    isServiceError: false,
  },
  [PopalErrorCodes.CURRENCY_MISMATCH]: {
    code: PopalErrorCodes.CURRENCY_MISMATCH,
    message: 'For UK deliveries, currency must be GBP.',
    isServiceError: false,
  },
  [PopalErrorCodes.COMMODITY_CODE_REQUIRED]: {
    code: PopalErrorCodes.COMMODITY_CODE_REQUIRED,
    message: 'Shipments to Northern Ireland require valid commodity codes for all items.',
    isServiceError: false,
  },
  [PopalErrorCodes.INVALID_ORDER_REFERENCE]: {
    code: PopalErrorCodes.INVALID_ORDER_REFERENCE,
    message: 'Order reference cannot contain spaces or line breaks.',
    isServiceError: false,
  },
  [PopalErrorCodes.PRODUCT_DESCRIPTION_REQUIRED]: {
    code: PopalErrorCodes.PRODUCT_DESCRIPTION_REQUIRED,
    message: 'Product description is required.',
    isServiceError: false,
  },
  [PopalErrorCodes.INVALID_PRODUCT_QUANTITY]: {
    code: PopalErrorCodes.INVALID_PRODUCT_QUANTITY,
    message: 'Product quantity must be between 1 and 999.',
    isServiceError: false,
  },
  [PopalErrorCodes.SYSTEM_ERROR]: {
    code: PopalErrorCodes.SYSTEM_ERROR,
    message: 'A system error occurred. Please try again or contact support if the issue persists.',
    isServiceError: true,
  },
  [PopalErrorCodes.ORDER_NOT_FOUND]: {
    code: PopalErrorCodes.ORDER_NOT_FOUND,
    message: 'Order not found. Please verify the order details.',
    isServiceError: false,
  },
  [PopalErrorCodes.SERVICE_PROVIDER_ERROR]: {
    code: PopalErrorCodes.SERVICE_PROVIDER_ERROR,
    message: 'Service provider configuration error. Please contact support.',
    isServiceError: false,
  },
  [PopalErrorCodes.ROUTING_FAILED]: {
    code: PopalErrorCodes.ROUTING_FAILED,
    message: 'Service routing failed. Please try again or contact support.',
    isServiceError: true,
  },
  [PopalErrorCodes.FUNCTION_EXECUTION_FAILED]: {
    code: PopalErrorCodes.FUNCTION_EXECUTION_FAILED,
    message: 'A processing error occurred. Please try again or contact support.',
    isServiceError: true,
  },
  [PopalErrorCodes.DATA_PROCESSING_ERROR]: {
    code: PopalErrorCodes.DATA_PROCESSING_ERROR,
    message: 'There was an issue processing your shipment data. Please check all required fields and try again.',
    isServiceError: false,
  },
  [PopalErrorCodes.ORDER_PLACEMENT_FAILED]: {
    code: PopalErrorCodes.ORDER_PLACEMENT_FAILED,
    message: 'Failed to place order with shipping provider. Please try again or contact support.',
    isServiceError: true,
  },
  [PopalErrorCodes.BILLING_CALCULATION_FAILED]: {
    code: PopalErrorCodes.BILLING_CALCULATION_FAILED,
    message: 'Unable to calculate shipping cost. Please check your product details and try again.',
    isServiceError: false,
  },
  [PopalErrorCodes.UNKNOWN_ERROR]: {
    code: PopalErrorCodes.UNKNOWN_ERROR,
    message: 'An error occurred while processing your shipment. Please contact support for assistance.',
    isServiceError: false,
  },
  [PopalErrorCodes.TRACKING_QUOTA_EXCEEDED]: {
    code: PopalErrorCodes.TRACKING_QUOTA_EXCEEDED,
    message: 'Your quota for tracking numbers has exceeded the limit. Please recharge or contact support.',
    isServiceError: false,
  },
};

/**
 * Maps a Popal error message to user-friendly information
 */
export const mapPopalErrorForUI = (errorMessage: string): PopalErrorMappingResult => {
  if (!errorMessage) {
    return {
      userMessage: POPAL_ERROR_MAPPINGS[PopalErrorCodes.UNKNOWN_ERROR].message,
      isServiceError: false,
      errorCode: PopalErrorCodes.UNKNOWN_ERROR,
    };
  }

  // Check for service down patterns
  const serviceDownPatterns = [
    /502 Bad Gateway/i,
    /503 Service Unavailable/i,
    /504 Gateway Timeout/i,
    /Connection reset/i,
    /Connection refused/i,
    /网络错误,SSL\/TLS握手失败/i,
    /TCP connection reset by peer/i,
    /Unexpected end of file from server/i,
    /Wait replication complete timeout/i,
    /Failed to obtain JDBC Connection/i,
    /GetConnectionTimeoutException/i,
    /系统错误: Connection refused/i,
    /I\/O error on POST request/i,
  ];

  const isServiceDown = serviceDownPatterns.some(pattern =>
    pattern.test(errorMessage)
  );

  if (isServiceDown) {
    const mapping = POPAL_ERROR_MAPPINGS[PopalErrorCodes.SERVICE_UNAVAILABLE];
    return {
      userMessage: mapping.message,
      isServiceError: mapping.isServiceError,
      errorCode: PopalErrorCodes.SERVICE_UNAVAILABLE,
    };
  }

  // Check for specific error patterns
  const errorPatterns: Array<{ pattern: RegExp; code: PopalErrorCodes }> = [
    // Phone validation - specific patterns first
    { pattern: /parameter params\.consignee\.phone is required/i, code: PopalErrorCodes.CONSIGNEE_PHONE_REQUIRED },
    { pattern: /params\.consignee\.phone cannot be empty/i, code: PopalErrorCodes.CONSIGNEE_PHONE_EMPTY },
    { pattern: /The consignee contact phone number is an invalid number/i, code: PopalErrorCodes.CONSIGNEE_PHONE_INVALID },
    { pattern: /parameter params\.shipper\.phone is required/i, code: PopalErrorCodes.SHIPPER_PHONE_REQUIRED },
    { pattern: /params\.shipper\.phone cannot be empty/i, code: PopalErrorCodes.SHIPPER_PHONE_EMPTY },
    { pattern: /The shipper contact phone number is an invalid number/i, code: PopalErrorCodes.SHIPPER_PHONE_INVALID },
    { pattern: /The contact phone number is an invalid number/i, code: PopalErrorCodes.PHONE_INVALID },
    // Name validation errors
    { pattern: /parameter params\.shipper\.name is required/i, code: PopalErrorCodes.SENDER_NAME_REQUIRED },
    { pattern: /receiver name length must be between 2 and 64/i, code: PopalErrorCodes.RECIPIENT_NAME_LENGTH },
    { pattern: /Receiver first and last name combined length is too long.*less than 40/i, code: PopalErrorCodes.RECIPIENT_NAME_TOO_LONG },
    { pattern: /Receiver first and last name combined length is too short.*greater than 2/i, code: PopalErrorCodes.RECIPIENT_NAME_TOO_SHORT },
    { pattern: /Sender first and last name combined length is too short.*greater than 2/i, code: PopalErrorCodes.SENDER_NAME_TOO_SHORT },
    // Address validation errors
    { pattern: /Receiver city length is too long.*less than 31/i, code: PopalErrorCodes.CITY_TOO_LONG },
    { pattern: /The Postcode is not a valid UK Postcode/i, code: PopalErrorCodes.INVALID_POSTCODE },
    { pattern: /first package did not transmit.*weight/i, code: PopalErrorCodes.WEIGHT_REQUIRED },
    { pattern: /TotalWeight must greater than 0/i, code: PopalErrorCodes.WEIGHT_INVALID },
    { pattern: /weight does not match/i, code: PopalErrorCodes.WEIGHT_MISMATCH },
    { pattern: /Northern Ireland.*require weight for all items/i, code: PopalErrorCodes.WEIGHT_REQUIRED_NI },
    { pattern: /country GB currency must need be GBP/i, code: PopalErrorCodes.CURRENCY_MISMATCH },
    { pattern: /Northern Ireland.*require.*commodity code/i, code: PopalErrorCodes.COMMODITY_CODE_REQUIRED },
    { pattern: /customer order number cannot.*spaces.*newline/i, code: PopalErrorCodes.INVALID_ORDER_REFERENCE },
    { pattern: /Product description.*not transmitted/i, code: PopalErrorCodes.PRODUCT_DESCRIPTION_REQUIRED },
    { pattern: /productNum.*between 1-999/i, code: PopalErrorCodes.INVALID_PRODUCT_QUANTITY },
    { pattern: /System error/i, code: PopalErrorCodes.SYSTEM_ERROR },
    { pattern: /order does not exist/i, code: PopalErrorCodes.ORDER_NOT_FOUND },
    { pattern: /Unable to obtain.*service provider/i, code: PopalErrorCodes.SERVICE_PROVIDER_ERROR },
    { pattern: /Service routing failed/i, code: PopalErrorCodes.ROUTING_FAILED },
    { pattern: /function execution failed/i, code: PopalErrorCodes.FUNCTION_EXECUTION_FAILED },
    { pattern: /parsing.*JSON object.*failed/i, code: PopalErrorCodes.DATA_PROCESSING_ERROR },
    { pattern: /Order Placement.*failed/i, code: PopalErrorCodes.ORDER_PLACEMENT_FAILED },
    { pattern: /Order Trial Calculation.*failed/i, code: PopalErrorCodes.BILLING_CALCULATION_FAILED },
    { pattern: /Trial Calculation.*billing failed/i, code: PopalErrorCodes.BILLING_CALCULATION_FAILED },
    // Tracking quota exceeded
    { pattern: /quota for tracking number has exceeded the limit, please recharge/i, code: PopalErrorCodes.TRACKING_QUOTA_EXCEEDED },
  ];

  for (const { pattern, code } of errorPatterns) {
    if (pattern.test(errorMessage)) {
      const mapping = POPAL_ERROR_MAPPINGS[code];
      return {
        userMessage: mapping.message,
        isServiceError: mapping.isServiceError,
        errorCode: code,
      };
    }
  }

  // Default fallback
  const mapping = POPAL_ERROR_MAPPINGS[PopalErrorCodes.UNKNOWN_ERROR];
  return {
    userMessage: mapping.message,
    isServiceError: mapping.isServiceError,
    errorCode: PopalErrorCodes.UNKNOWN_ERROR,
  };
};

/**
 * Checks if an error is a Popal related error
 */
export const isPopalError = (errorMessage: string): boolean => {
  const popalIndicators = [
    'gdf delivery errorMsg',
    'apollo_fulfill-oms',
    'CNGDF_AE_UK_RM',
    'Order Order',
    'Order Placement',
    'Order Trial',
    '获取订单标签出错',
    'params.consignee',
    'params.shipper',
    'P-011-',
  ];

  return popalIndicators.some(indicator =>
    errorMessage.toLowerCase().includes(indicator.toLowerCase())
  );
};
